import React from 'react';
import { gradeBadgeClass } from '../../constants/oldMobileQc';
import {
  effectiveFunctionalQcForUnit,
  sortedFunctionalQcEntries,
  formatFunctionalQcLabel,
  physicalSpecEntriesForQc,
  formatPhysicalSpecLabel,
  formatPhysicalSpecValue,
  isQcValuePassing,
  isQcValueIssue,
  normalizeQcObject
} from '../../utils/oldMobileQcDisplay';

/**
 * UnitQcViewerModal — full Old Mobile QC (functional + physical), customer site jaisa.
 *
 * Props:
 *   unitQcViewer — {
 *     title, imei1, imei2?,
 *     qc, listingQc?, productSpecs?, unitConditionGrade?, unitStatus?
 *   } | null
 *   onClose()
 */
export default function UnitQcViewerModal({ unitQcViewer, onClose }) {
  if (!unitQcViewer) return null;

  const mergedQc = effectiveFunctionalQcForUnit(unitQcViewer.qc, unitQcViewer.listingQc);
  const functionalRows = sortedFunctionalQcEntries(mergedQc);
  const unitGrade = String(unitQcViewer.unitConditionGrade || '').trim();
  const listingGrade = String(unitQcViewer.productSpecs?.conditionGrade || '').trim();
  const displayGrade = unitGrade || listingGrade;

  const physicalRaw = physicalSpecEntriesForQc(unitQcViewer.productSpecs || {});
  const physicalRows = displayGrade
    ? physicalRaw.filter(([k]) => k !== 'conditionGrade')
    : physicalRaw;

  const conditionNote = String(
    normalizeQcObject(unitQcViewer.qc)?.conditionNote || ''
  ).trim();

  const status = unitQcViewer.unitStatus || '';
  const storedKeys = Object.keys(normalizeQcObject(unitQcViewer.qc) || {}).filter(
    (k) => k !== 'conditionNote' && !['conditionGrade', 'scratchesLevel', 'screenCondition'].includes(k)
  );
  const showsMergedDefaults =
    functionalRows.length > storedKeys.filter((k) => unitQcViewer.qc?.[k] != null).length;

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title mb-1">
                Unit QC — {unitQcViewer.title}
              </h5>
              <div className="small text-muted">
                IMEI <code>{unitQcViewer.imei1}</code>
                {unitQcViewer.imei2 ? (
                  <> · IMEI 2 <code>{unitQcViewer.imei2}</code></>
                ) : null}
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
              {displayGrade ? (
                <span className={`badge ${gradeBadgeClass(displayGrade)} fs-6`}>
                  Grade {displayGrade}
                </span>
              ) : null}
              {status === 'sold' ? (
                <span className="badge bg-danger">Sold — QC record preserved</span>
              ) : status === 'available' ? (
                <span className="badge bg-success">In stock</span>
              ) : status === 'transferred_out' ? (
                <span className="badge bg-warning text-dark">Transferred out</span>
              ) : null}
            </div>

            {physicalRows.length > 0 && (
              <section className="mb-4">
                <h6 className="fw-semibold border-bottom pb-2 mb-2">
                  Physical condition &amp; accessories
                </h6>
                <p className="small text-muted mb-2">
                  Scratches, screen, battery, in-box items — listing/QC time par record hua.
                </p>
                <div className="row g-2">
                  {physicalRows.map(([key, value]) => (
                    <div key={key} className="col-md-6">
                      <div className="border rounded p-2 h-100 bg-light">
                        <div className="small text-muted">{formatPhysicalSpecLabel(key)}</div>
                        <div className="fw-semibold">{formatPhysicalSpecValue(key, value)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {functionalRows.length > 0 ? (
              <section className="mb-3">
                <h6 className="fw-semibold border-bottom pb-2 mb-2">
                  Functional checks
                </h6>
                {showsMergedDefaults ? (
                  <p className="small text-muted mb-2">
                    ✓ = working. Jo unit par mark nahi tha, listing/default se pass mana gaya (customer site
                    jaisa).
                  </p>
                ) : null}
                <div className="row g-2">
                  {functionalRows.map(([key, value]) => {
                    const pass = isQcValuePassing(value);
                    const issue = isQcValueIssue(value);
                    return (
                      <div key={key} className="col-md-6 col-lg-4">
                        <div
                          className={`border rounded p-2 h-100 ${
                            pass ? 'border-success bg-success bg-opacity-10' : issue ? 'border-warning bg-warning bg-opacity-10' : ''
                          }`}
                        >
                          <div className="d-flex align-items-start gap-2">
                            <span
                              className={`fw-bold ${pass ? 'text-success' : issue ? 'text-danger' : 'text-muted'}`}
                              aria-hidden="true"
                            >
                              {pass ? '✓' : issue ? '✗' : '—'}
                            </span>
                            <div className="small">
                              <div className="fw-semibold">{formatFunctionalQcLabel(key)}</div>
                              {pass ? (
                                <span className="text-success">OK / Working</span>
                              ) : issue ? (
                                <span className="text-dark">
                                  {typeof value === 'string' && value !== 'issue'
                                    ? value
                                    : 'Issue reported'}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : (
              <div className="alert alert-info small">
                Is unit par functional QC record nahi mila. Edit product se QC save karein.
              </div>
            )}

            {conditionNote ? (
              <section className="mb-2">
                <h6 className="fw-semibold border-bottom pb-2 mb-2">Extra condition note</h6>
                <div className="border rounded p-3 bg-light small">{conditionNote}</div>
              </section>
            ) : null}

            {!physicalRows.length && !functionalRows.length && !conditionNote ? (
              <div className="alert alert-warning mb-0">
                Koi QC detail save nahi hai is IMEI par.
              </div>
            ) : null}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
